import axios from "axios";
import { toast } from "sonner";
import { safeLocal } from "./safeStorage";
import { decryptIdentities } from "./identity";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 180000 });

api.interceptors.request.use((config) => {
  const token = safeLocal.get("ss_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Transparently decrypt encrypted identity blobs (name/headline) in any
// response, so components keep reading `.name` / `.headline` unchanged.
// On an expired/invalid session (401), clear the token and bounce the user to
// sign in again instead of surfacing a raw "Token expired" error mid-action.
let lastSessionExpired = 0;
api.interceptors.response.use(
  async (response) => {
    try { await decryptIdentities(response.data); } catch {}
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isAuthRoute = url.includes("/auth/");
    // Any 401 on a protected (non-/auth/) route means the session is stale,
    // the backend only ever returns 401 from token verification (403 is used
    // for permission failures), so we don't gate on the exact detail wording.
    if (status === 401 && !isAuthRoute && Date.now() - lastSessionExpired > 3000) {
      lastSessionExpired = Date.now();
      safeLocal.remove("ss_token");
      try { toast.error("Your session expired, please sign in again."); } catch {}
      window.dispatchEvent(new CustomEvent("ss:session-expired"));
    }
    // No response at all = request timed out or the server was unreachable
    // (e.g. a brief cold-start / connection drop). Fire a network-down event so
    // the global ConnectionBanner can show "Reconnecting…" and poll for recovery.
    if (!error?.response) {
      try { window.dispatchEvent(new CustomEvent("ss:network-down")); } catch {}
    }
    return Promise.reject(error);
  }
);

// Lightweight server reachability check (used by the reconnect banner + retry).
export const pingServer = async () => {
  try {
    const r = await fetch(`${API}/`, { method: "GET", cache: "no-store" });
    return r.ok;
  } catch { return false; }
};

// Resolve(true) as soon as the server is reachable again (via the ss:network-up
// event the banner fires, or our own poll), or resolve(false) after timeoutMs.
export const waitForServerUp = (timeoutMs = 60000) =>
  new Promise((resolve) => {
    let done = false;
    const finish = (ok) => { if (done) return; done = true; cleanup(); resolve(ok); };
    const onUp = () => finish(true);
    window.addEventListener("ss:network-up", onUp);
    const poll = setInterval(async () => { if (await pingServer()) finish(true); }, 3000);
    const to = setTimeout(() => finish(false), timeoutMs);
    function cleanup() { window.removeEventListener("ss:network-up", onUp); clearInterval(poll); clearTimeout(to); }
  });

export const mediaUrl = (path) => {
  const token = safeLocal.get("ss_token");
  return `${API}/files/${path}?auth=${token}`;
};

// Route YouTube thumbnails through our server so Google never sees a member's IP.
export const streamThumb = (rawUrl) => {
  const token = safeLocal.get("ss_token");
  return `${API}/stream/thumb?u=${encodeURIComponent(rawUrl)}&auth=${token}`;
};

export const CATEGORY_LABELS = {
  community: "Community",
  womens_circle: "Women's Circle",
  mens_circle: "Men's Circle",
  homeschooling: "Homeschooling / Unschooling",
  business: "Business",
  digital: "Digital Sovereignty",
  financial: "Financial",
  art: "Art",
  infrastructure: "Signal Infrastructure",
  trade: "Trading Services",
  soul_growth: "Soul Growth",
  health: "Health & Healing",
  earth: "Earth Sovereignty",
  enlightenment: "Share Inspirations",
  life: "Life!",
  astrology_numerology: "Numerology / Astrology",
  tarot: "Tarot",
  matrix_news: "Matrix (News)",
  business_directory: "Business Directory",
};

export const CATEGORY_COLORS = {
  community: "#A1A1AA",
  womens_circle: "#E4A8C4",
  mens_circle: "#8AB4D8",
  homeschooling: "#E3C08D",
  business: "#FFFFFF",
  digital: "#71717A",
  financial: "#F4F4F5",
  art: "#14A89A",
  infrastructure: "#52525B",
  trade: "#E4E4E7",
  soul_growth: "#14A89A",
  health: "#5FD3A6",
  earth: "#C1663F",
  enlightenment: "#2BC4B5",
  life: "#34D399",
  astrology_numerology: "#A855F7",
  tarot: "#C084FC",
  matrix_news: "#9CA3AF",
  business_directory: "#C4B5FD",
};

export const CATEGORY_DESC = {
  community: "Community-centered posts, events, and community building, including homesteading, parallel and intentional communities, food sovereignty and daily farming life, and the new community systems we're growing together.",
  womens_circle: "A circle held for women: childbirth, monthly rhythm, mothering, women's health, and the conversations that live here.",
  mens_circle: "A coherent space for brothers to connect, share and be real: brotherhood, fatherhood, men's health, and purpose.",
  homeschooling: "The struggles and beauty of raising sovereign, discerning souls who will carry our work into the future: homeschooling, unschooling, and conscious parenting.",
  business: "Running sovereign businesses, sovereignty within the business world, and new ways for entrepreneurs to work.",
  digital: "Digital privacy, encryption, and sovereign security around AI and government overreach, plus building new digital systems.",
  financial: "New financial systems, trading, trusts, strawman, and reclaiming financial sovereignty.",
  art: "All art welcome.",
  infrastructure: "This is the hub for all Sovereign Signal business, ideas, conversation, suggestions.",
  trade: "Offers of trading services, and the future of trading energy instead of money.",
  soul_growth: "Soul growth, expansion, and the personal journey. The deeper content lives here.",
  health: "Body and mind. Holistic, naturopathic, herbal alchemy, breath work, fitness and movement, mind health, all things healing!",
  earth: "Regenerative soil and farming, permaculture, seed saving, energetic grid / leyline work. Water stewardship, foraging, natural building.",
  enlightenment: "The lighter side of sovereignty: the fun, the funny, the downright comical. Joy, laughter, feel-good wins, everyday inspiration, and Mother Earth moments that remind us lived sovereignty is meant to feel good. Bring your play, your humor, and your beauty.",
  life: "This is the area for your personal posts, sharing experiences or moments that are personal but don't feel they fit any other category.",
  astrology_numerology: "Daily reads and deeper concepts: numerology and astrology posts and offerings.",
  tarot: "Tarot pulls, spreads, readings, and offerings: daily cards and the deeper meanings behind them.",
  matrix_news: "All matrix-related material.",
  business_directory: "For business owners and entrepreneurs who want to share their business.",
};

// Realms are grouped into a handful of parent groups for cleaner browsing.
export const REALM_GROUPS = [
  { key: "circles", label: "Circles", domains: ["community", "womens_circle", "mens_circle", "homeschooling"] },
  { key: "spirit_body", label: "Spirit & Body", domains: ["soul_growth", "health", "earth", "astrology_numerology", "enlightenment", "tarot"] },
  { key: "systems", label: "Systems & Sovereignty", domains: ["business_directory", "business", "financial", "digital", "infrastructure", "trade"] },
  { key: "culture_life", label: "Culture & Life", domains: ["art", "life", "matrix_news"] },
];

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
